import { v4 as uuidv4 } from "uuid";
import { create } from "zustand";
import { Plan, PlanDetail } from "../../../types/plan";
import config from "../../../utils/config";
import {
  AIActionResponse,
  callChatApi,
  ChatMessage,
  ChatSuccessResponse,
} from "../types/aiChatClient";
import { highlightSearchResults, aiSearchCallback } from "../utils/aiChatUtils";

// Determine the API URL based on environment
const API_BASE_URL = config.ServerBackEndUrl;
const CHAT_API_URL = `${API_BASE_URL}/ai/chat`;

export interface SpacesName {
  name: string;
  dbas: string[];
}
export interface AIChat {
  messages: ChatMessage[];
  historyMessagesToSend: ChatMessage[];
  isLoading: boolean;
  isSearching: boolean;
  error: string | null;
  currentAction: AIActionResponse | null;
  currentHighlight: string | null;
  clearCurrentHighlight: () => void;
  plansInfo: any | null;
  distanceData: any | null;

  // Actions
  sendMessage: (
    prompt: string,
    spacesNames: SpacesName[],
    selectedPlan?: Plan,
    selectedPlanDetail?: PlanDetail,
    useGlobalMode?: boolean,
    selectedSpaces?: string[]
  ) => Promise<void>;

  clearError: () => void;
  internalCADViewerAICallback: (chatMessage: ChatMessage) => void;
  setPlansInfoAndDistanceData: (plansInfo: any, distanceData: any) => void;
  clearHistoryMessagesToSend: () => void;
}

export const useAIChatStore = create<AIChat>((set, get) => ({
  messages: [],
  historyMessagesToSend: [],
  isLoading: false,
  isSearching: false,
  error: null,
  currentAction: null,
  currentHighlight: null,
  plansInfo: null,
  distanceData: null,

  setPlansInfoAndDistanceData: (plansInfo, distanceData) => {
    set({ plansInfo, distanceData });
  },

  clearError: () => {
    set({ error: null, currentAction: null });
  },

  internalCADViewerAICallback: (chatMessage: ChatMessage) => {
    const { currentHighlight } = get();

    if (!chatMessage.result) return;

    if (currentHighlight !== chatMessage.uuid) {
      // Utiliser la fonction utilitaire pour faire le highlighting
      highlightSearchResults(chatMessage.result);
      set({ currentHighlight: chatMessage.uuid ?? null });
    } else {
      // Effacer les highlights existants
      highlightSearchResults({ targetPlanInfo: undefined, searchResults: [] });
      set({ currentHighlight: null });
    }
  },

  sendMessage: async (
    prompt: string,
    spacesNames: SpacesName[],
    selectedPlan?: Plan,
    selectedPlanDetail?: PlanDetail,
    useGlobalMode: boolean = false,
    selectedSpaces?: string[]
  ) => {
    if (!prompt.trim()) return;

    const { plansInfo, distanceData, historyMessagesToSend } = get();

    const metadata = {
      searchMode: useGlobalMode ? ("global" as const) : ("current" as const),
      selectedSpaces: selectedSpaces || [],
      planInfo: !useGlobalMode && selectedPlan && selectedPlanDetail ? {
        propertyName: selectedPlan.propertyName,
        planName: selectedPlanDetail.planName,
      } : undefined,
      timestamp: new Date().toISOString(),
    };

    const newUserMessage: ChatMessage = {
      uuid: uuidv4(),
      role: "user",
      content: prompt,
      metadata,
    };

    // Keep current messages state to pass as history
    const historyToSend = historyMessagesToSend;

    // Optimistically update UI with user message
    set((state) => ({
      messages: [...state.messages, newUserMessage],
      historyMessagesToSend: [...state.historyMessagesToSend, newUserMessage],
      isLoading: true,
      error: null,
      currentAction: null,
    }));

    try {
      console.log(
        "Calling API at:",
        CHAT_API_URL,
        "with prompt:",
        prompt,
        "and history:",
        historyToSend.map((msg) => ({
          role: msg.role,
          content: msg.content,
        }))
      );

      const requestUuid = uuidv4();
      const result: ChatSuccessResponse = await callChatApi(
        CHAT_API_URL,
        prompt,
        historyToSend.map((msg) => ({
          uuid: undefined,
          role: msg.role,
          content: msg.content,
        })),
        spacesNames
      );

      console.log("API Response:", result);

      // Determine message content and potential search execution
      if (
        result.aiResponse &&
        result.aiResponse.action_type !== "request_more_info"
      ) {
        // Action response - Prepare message, but don't add yet.
        const descriptionContent =
          result.aiResponse.description || "Performing the requested action...";

        const preliminaryAssistantMessage: ChatMessage = {
          uuid: requestUuid,
          role: "assistant",
          content: descriptionContent,
          rawContent: result.rawResponse,
        };

        // Indicate search is starting
        set((state) => ({
          messages: [...state.messages, preliminaryAssistantMessage],
          isLoading: false,
          isSearching: true,
          currentAction: result.aiResponse,
        }));

        // Process search results using the utility function
        const searchResult = aiSearchCallback(
          result.rawResponse,
          plansInfo,
          distanceData
        );

        // Create the final message WITH search results
        const finalAssistantMessage: ChatMessage = {
          ...preliminaryAssistantMessage,
          result: searchResult,
        };

        // Add the final message to the state
        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.uuid === requestUuid ? finalAssistantMessage : msg
          ),
          isSearching: false,
        }));

        // Apply highlighting
        setTimeout(() => {
          get().internalCADViewerAICallback(finalAssistantMessage);
        }, 200);

        console.log(
          "Simulated search results added to message:",
          finalAssistantMessage.result
        );
      } else {
        // No action or 'request_more_info' - just display raw/description response
        const assistantContent =
          result.aiResponse?.description || result.rawResponse;

        const newAssistantMessage: ChatMessage = {
          uuid: requestUuid,
          role: "assistant",
          content: assistantContent,
          rawContent: result.rawResponse,
        };

        set((state) => ({
          messages: [...state.messages, newAssistantMessage],
          isLoading: false,
          currentAction: result.aiResponse,
        }));
      }
    } catch (err) {
      console.error("Chat API Error in store:", err);
      const errorMessage =
        err instanceof Error ? err.message : "An unknown error occurred.";

      // Create an error message to display in chat
      const errorAssistantMessage: ChatMessage = {
        uuid: uuidv4(),
        role: "assistant",
        content: `Sorry, I encountered an error: ${errorMessage}`,
      };

      set((state) => ({
        messages: [...state.messages, errorAssistantMessage],
        isLoading: false,
        isSearching: false,
        error: errorMessage,
      }));
    }
  },
  clearHistoryMessagesToSend: () => {
    set({ historyMessagesToSend: [], messages: [] });
  },
  clearCurrentHighlight: () => {
    set({ currentHighlight: null });
  },
}));
